/* Expense > Admin — COA custom-attribute administration (Phase 1). Wired to
   /v1/expense/coa/attributes. Admins define the accounting attributes a GL line may
   carry; a `required` attribute is enforced by the server-side validation gate before
   a report can be submitted/posted. LIST-kind attributes get a managed set of allowed
   values, expandable inline. Entity-scoped server-side. */
import { CaretDownIcon, CaretRightIcon, PlusIcon } from '@phosphor-icons/react';
import { type FormEvent, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@andavo/web-ui';

import {
  useAddAttributeValue,
  useAttributeValues,
  useCoaAttributes,
  useCreateAttribute,
  type CoaAttribute,
  type CoaAttributeKind,
} from './hooks/use-coa';
import { AdminPeople } from './AdminPeople';
import { Field, Input, Select } from './ui/field';

export function AdminScreen() {
  const { data: attributes = [], isLoading, isError } = useCoaAttributes();

  return (
    <div className="flex flex-col gap-10">
      <section className="flex flex-col gap-4">
        <h2 className="font-sans font-semibold text-content-primary text-lg">
          People
        </h2>
        <AdminPeople />
      </section>

      <section className="flex flex-col gap-4">
        <h2 className="font-sans font-semibold text-content-primary text-lg">
          Chart of accounts
        </h2>
        <CreateAttributeForm />

        <Card className="overflow-hidden p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-8" />
                <TableHead>Attribute</TableHead>
                <TableHead>Key</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Required</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attributes.map((attr) => (
                <AttributeRow key={attr.attributeId} attr={attr} />
              ))}
              {isLoading && (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={5}
                    className="py-10 text-center text-content-secondary"
                  >
                    Loading attributes…
                  </TableCell>
                </TableRow>
              )}
              {isError && !isLoading && (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={5}
                    className="py-10 text-center text-content-danger"
                  >
                    Couldn’t load attributes.
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && !isError && attributes.length === 0 && (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={5}
                    className="py-10 text-center text-content-secondary"
                  >
                    No custom attributes yet. Add one above.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      </section>
    </div>
  );
}

function AttributeRow({ attr }: { attr: CoaAttribute }) {
  const [open, setOpen] = useState(false);
  const isList = attr.kind === 'LIST';

  return (
    <>
      <TableRow>
        <TableCell>
          {isList ? (
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              aria-label={open ? 'Collapse values' : 'Expand values'}
              className="text-content-secondary hover:text-content-primary"
            >
              {open ? (
                <CaretDownIcon className="size-icon-sm" />
              ) : (
                <CaretRightIcon className="size-icon-sm" />
              )}
            </button>
          ) : null}
        </TableCell>
        <TableCell className="font-inter font-medium text-content-primary">
          {attr.label}
        </TableCell>
        <TableCell className="text-content-secondary">
          <code className="font-mono text-xs">{attr.key}</code>
        </TableCell>
        <TableCell>
          <Badge
            variant={isList ? 'blue' : 'default'}
            intent="background"
            borderRadius="round"
          >
            {attr.kind}
          </Badge>
        </TableCell>
        <TableCell>
          {attr.required ? (
            <Badge variant="warning" intent="background" borderRadius="round">
              Required
            </Badge>
          ) : (
            <span className="font-inter text-content-secondary text-sm">
              Optional
            </span>
          )}
        </TableCell>
      </TableRow>
      {isList && open && (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={5} className="bg-surface-midground">
            <AttributeValues attributeId={attr.attributeId} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function AttributeValues({ attributeId }: { attributeId: number }) {
  const { data: values = [], isLoading } = useAttributeValues(attributeId);
  const addValue = useAddAttributeValue(attributeId);
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');

  const add = (e: FormEvent) => {
    e.preventDefault();
    if (!value.trim()) return;
    addValue.mutate(
      { value, label: label || value },
      {
        onSuccess: () => {
          setValue('');
          setLabel('');
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-3 py-2">
      {isLoading ? (
        <p className="font-inter text-content-secondary text-sm">
          Loading values…
        </p>
      ) : values.length === 0 ? (
        <p className="font-inter text-content-secondary text-sm">
          No values yet.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {values.map((v) => (
            <Badge key={v.valueId} variant="default" intent="background">
              {v.label}
            </Badge>
          ))}
        </div>
      )}
      <form onSubmit={add} className="flex flex-wrap items-end gap-2">
        <Field
          label="Value"
          htmlFor={`val-${attributeId}`}
          className="min-w-40"
        >
          <Input
            id={`val-${attributeId}`}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="e.g. CC-100"
          />
        </Field>
        <Field
          label="Label"
          htmlFor={`lbl-${attributeId}`}
          className="min-w-40"
        >
          <Input
            id={`lbl-${attributeId}`}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Sales — West"
          />
        </Field>
        <Button
          type="submit"
          variant="secondary"
          size="sm"
          disabled={addValue.isPending}
        >
          <PlusIcon className="me-2 size-icon-sm" />
          Add value
        </Button>
      </form>
    </div>
  );
}

function CreateAttributeForm() {
  const create = useCreateAttribute();
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState<CoaAttributeKind>('TEXT');
  const [required, setRequired] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!key.trim() || !label.trim()) return;
    create.mutate(
      { key, label, kind, required },
      {
        onSuccess: () => {
          setKey('');
          setLabel('');
          setKind('TEXT');
          setRequired(false);
        },
        onError: (err) =>
          setError(err instanceof Error ? err.message : 'Create failed.'),
      },
    );
  };

  return (
    <Card className="p-5">
      <h2 className="mb-4 font-sans font-semibold text-content-primary text-base">
        New custom attribute
      </h2>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Key" htmlFor="attr-key" hint="Stable machine key">
            <Input
              id="attr-key"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="cost_center"
            />
          </Field>
          <Field label="Label" htmlFor="attr-label">
            <Input
              id="attr-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Cost Center"
            />
          </Field>
          <Field label="Kind" htmlFor="attr-kind">
            <Select
              id="attr-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as CoaAttributeKind)}
            >
              <option value="TEXT">Free text</option>
              <option value="LIST">Managed list</option>
            </Select>
          </Field>
          <Field label="Required" htmlFor="attr-required">
            <label className="flex h-10 items-center gap-2 font-inter text-content-primary text-sm">
              <input
                id="attr-required"
                type="checkbox"
                checked={required}
                onChange={(e) => setRequired(e.target.checked)}
                className="size-4 accent-[hsl(var(--brand-500))]"
              />
              Enforce on every line
            </label>
          </Field>
        </div>
        {error && (
          <p className="font-inter text-content-danger text-sm">{error}</p>
        )}
        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={create.isPending}
          >
            <PlusIcon className="me-2 size-icon-sm" />
            Add attribute
          </Button>
        </div>
      </form>
    </Card>
  );
}
